/**
 * Add Voice Form
 *
 * Form for adding voices to the database with enrichment data.
 * Pre-fills provider data and allows manual input of metadata.
 *
 * Acceptance Criteria: AC 28, 34
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  useCreateVoice,
  useGenerateVoiceAttributes,
  useTestVoice,
  type TTSProvider,
  type VoiceGender,
} from '../services/voiceApi';
import { useInitialData } from 'contexts/InitialDataContext';
import { useLocales } from 'features/dictionaries/services/localesApi';
import logger from 'utils/logger';

interface AddVoiceFormProps {
  isOpen: boolean;
  onClose: () => void;
  provider: TTSProvider;
  providerVoiceId: string;
  voiceName: string;
  metadata?: Record<string, unknown>;
  languages?: string[];
}

// Stable empty array reference to prevent infinite re-renders
const EMPTY_LANGUAGES: string[] = [];

export function AddVoiceForm({
  isOpen,
  onClose,
  provider,
  providerVoiceId,
  voiceName,
  metadata,
  languages = EMPTY_LANGUAGES,
}: AddVoiceFormProps) {
  const { state: initialDataState } = useInitialData();

  // Get language options from initial data
  const languageOptions = useMemo(() => {
    if (!initialDataState.data?.languages) return [];
    // Map to simple { code, name } structure for easier use
    return initialDataState.data.languages.map((lang) => ({
      code: lang.code,
      name: lang.name,
    }));
  }, [initialDataState.data?.languages]);

  // Filter language options to only show languages supported by this TTS voice
  const filteredLanguageOptions = useMemo(() => {
    if (!languages || languages.length === 0) {
      // If no languages provided from TTS, show all available languages
      return languageOptions;
    }
    // Only show languages that both exist in our language list AND are supported by the TTS voice
    return languageOptions.filter((lang) => languages.includes(lang.code));
  }, [languageOptions, languages]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [gender, setGender] = useState<VoiceGender>('unclear');
  const [localeCode, setLocaleCode] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingSample, setIsGeneratingSample] = useState(false);
  const hasGeneratedRef = useRef(false);

  // Fetch available locales for locale assignment
  const { data: allLocales } = useLocales();

  // Filter locales to only show those matching selected languages
  const availableLocales = useMemo(() => {
    if (!allLocales || selectedLanguages.length === 0) return [];
    return allLocales.filter((locale) => selectedLanguages.includes(locale.languageCode));
  }, [allLocales, selectedLanguages]);

  const createVoiceMutation = useCreateVoice();
  const generateAttributesMutation = useGenerateVoiceAttributes();
  const testVoiceMutation = useTestVoice();

  const descriptionMinLength = 10;
  const descriptionMaxLength = 1000;
  const nameMaxLength = 100;
  const descriptionLength = description.length;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setDescription('');
      setSelectedLanguages([]);
      setGender('unclear');
      setLocaleCode('');
      setErrors({});
      setIsGenerating(false);
      setIsGeneratingSample(false);
      hasGeneratedRef.current = false; // Reset generation flag
    } else {
      // Initialize name with voiceName when opening
      setName(voiceName);

      // Prefill languages from TTS provider voice data
      if (languages && languages.length > 0) {
        // Filter to only include languages that exist in our language options
        const validLanguages = languages.filter((lang) =>
          languageOptions.some((opt) => opt.code === lang)
        );
        setSelectedLanguages(validLanguages);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, voiceName, languages]);

  // Auto-generate voice attributes when form opens with metadata (only once)
  useEffect(() => {
    const generateAttributes = async () => {
      // Only generate once per modal opening
      if (!isOpen || !metadata || hasGeneratedRef.current) return;

      hasGeneratedRef.current = true; // Mark as generated
      setIsGenerating(true);

      try {
        const request = {
          name: voiceName,
          language: (metadata.language as string) || 'en',
          metadata: metadata,
        };

        const result = await generateAttributesMutation.mutateAsync(request);

        // Auto-populate gender, description, and locale
        setGender(result.gender as VoiceGender);
        setDescription(result.description);
        if (result.localeCode) {
          setLocaleCode(result.localeCode);
        }

        toast.success('AI-generated voice attributes loaded!');
      } catch (error) {
        logger.error('Failed to generate voice attributes:', error);
        toast.error('Failed to auto-generate attributes. Please fill manually.');
      } finally {
        setIsGenerating(false);
      }
    };

    generateAttributes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, metadata, voiceName]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Voice name is required';
    } else if (name.length > nameMaxLength) {
      newErrors.name = `Voice name must be less than ${nameMaxLength} characters`;
    }

    if (description.length < descriptionMinLength) {
      newErrors.description = `Description must be at least ${descriptionMinLength} characters`;
    } else if (description.length > descriptionMaxLength) {
      newErrors.description = `Description must be no more than ${descriptionMaxLength} characters`;
    }

    if (selectedLanguages.length === 0) {
      newErrors.languages = 'At least one language is required';
    }

    if (!gender) {
      newErrors.gender = 'Gender is required';
    }

    if (!localeCode) {
      newErrors.locale = 'Regional accent (locale) is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddLanguage = (languageCode: string) => {
    if (!selectedLanguages.includes(languageCode)) {
      setSelectedLanguages([...selectedLanguages, languageCode]);
    }
  };

  const handleRemoveLanguage = (languageCode: string) => {
    setSelectedLanguages(selectedLanguages.filter((code) => code !== languageCode));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Step 1: Generate sample audio first
      setIsGeneratingSample(true);
      toast.loading('Generating sample audio...', { id: 'sample-generation' });

      let sampleAudioUrl: string | undefined;
      try {
        const sampleResult = await testVoiceMutation.mutateAsync({
          providerId: provider.id,
          request: {
            providerVoiceId,
            text: 'Hello, this is a sample of my voice. I hope you enjoy listening to me speak.',
          },
        });
        sampleAudioUrl = sampleResult.audioUrl;
        toast.success('Sample audio generated!', { id: 'sample-generation' });
      } catch (sampleErr) {
        logger.error('Failed to generate sample audio:', sampleErr);
        toast.error('Failed to generate sample audio, continuing anyway...', {
          id: 'sample-generation',
        });
      } finally {
        setIsGeneratingSample(false);
      }

      // Step 2: Create voice with all data including sample
      await createVoiceMutation.mutateAsync({
        providerId: provider.id,
        providerVoiceId,
        name: name.trim(),
        description: description.trim(),
        languageCodes: selectedLanguages,
        gender,
        localeCode,
        sampleAudioUrl,
      });

      toast.success(`Voice "${name}" added successfully!`);
      onClose();
    } catch (err) {
      setErrors({
        submit: err instanceof Error ? err.message : 'Failed to add voice',
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-testid="add-voice-form"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold" data-testid="form-header">
              Add Voice to Database
            </h2>
            {isGenerating && (
              <p className="text-sm text-blue-600 mt-1" data-testid="generating-indicator">
                🤖 AI is generating voice attributes...
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            data-testid="close-button"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Read-only fields */}
          <div className="mb-6 flex flex-wrap gap-2">
            <span
              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
              data-testid="provider-badge"
            >
              Provider: {provider.name}
            </span>
            <span
              className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
              data-testid="voice-id-badge"
            >
              ID: {providerVoiceId}
            </span>
          </div>

          {/* Voice Name */}
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Voice Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={nameMaxLength}
              className={`w-full px-3 py-2 border ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter voice name"
              data-testid="name-input"
              disabled={isGenerating}
            />
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-gray-500">Max {nameMaxLength} characters</span>
              <span
                className={`text-xs ${
                  name.length > nameMaxLength ? 'text-red-600' : 'text-gray-500'
                }`}
                data-testid="name-counter"
              >
                {name.length} / {nameMaxLength}
              </span>
            </div>
            {errors.name && (
              <p className="text-red-500 text-xs mt-1" data-testid="name-error">
                {errors.name}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              className={`w-full px-3 py-2 border ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Describe voice personality, characteristics, accent, age, tone, and suitable roles..."
              data-testid="description-input"
              disabled={isGenerating}
            />
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-gray-500">
                {descriptionMinLength}-{descriptionMaxLength} characters required
              </span>
              <span
                className={`text-xs ${
                  descriptionLength < descriptionMinLength ||
                  descriptionLength > descriptionMaxLength
                    ? 'text-red-600'
                    : 'text-gray-500'
                }`}
                data-testid="description-counter"
              >
                {descriptionLength} / {descriptionMaxLength}
              </span>
            </div>
            {errors.description && (
              <p className="text-red-500 text-xs mt-1" data-testid="description-error">
                {errors.description}
              </p>
            )}
          </div>

          {/* Languages */}
          <div className="mb-4">
            <label
              htmlFor="language-select"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Languages <span className="text-red-500">*</span>
            </label>
            <select
              id="language-select"
              onChange={(e) => {
                if (e.target.value) {
                  handleAddLanguage(e.target.value);
                  e.target.value = '';
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
              data-testid="language-select"
            >
              <option value="">Select a language...</option>
              {filteredLanguageOptions.map((lang) => (
                <option
                  key={lang.code}
                  value={lang.code}
                  disabled={selectedLanguages.includes(lang.code)}
                >
                  {lang.name}
                </option>
              ))}
            </select>

            {/* Selected Languages Badges */}
            <div className="flex flex-wrap gap-2" data-testid="selected-languages">
              {selectedLanguages.map((code) => {
                const lang = languageOptions.find((l) => l.code === code);
                return (
                  <span
                    key={code}
                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center"
                    data-testid={`language-badge-${code}`}
                  >
                    {lang?.name || code}
                    <button
                      type="button"
                      onClick={() => handleRemoveLanguage(code)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                      data-testid={`remove-language-${code}`}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
            {errors.languages && (
              <p className="text-red-500 text-xs mt-1" data-testid="languages-error">
                {errors.languages}
              </p>
            )}
          </div>

          {/* Gender */}
          <div className="mb-4">
            <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              id="gender"
              value={gender}
              onChange={(e) => setGender(e.target.value as VoiceGender)}
              className={`w-full px-3 py-2 border ${
                errors.gender ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              data-testid="gender-select"
              disabled={isGenerating}
            >
              <option value="unclear">Unclear</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            {errors.gender && (
              <p className="text-red-500 text-xs mt-1" data-testid="gender-error">
                {errors.gender}
              </p>
            )}
          </div>

          {/* Locale (Regional Accent) */}
          <div className="mb-6">
            <label htmlFor="locale" className="block text-sm font-medium text-gray-700 mb-2">
              Regional Accent (Locale) <span className="text-red-500">*</span>
            </label>
            <select
              id="locale"
              value={localeCode}
              onChange={(e) => setLocaleCode(e.target.value)}
              className={`w-full px-3 py-2 border ${
                errors.locale ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              data-testid="locale-select"
              disabled={isGenerating || selectedLanguages.length === 0}
            >
              <option value="">-- Select regional accent --</option>
              {availableLocales.map((locale) => (
                <option key={locale.localeCode} value={locale.localeCode}>
                  {locale.name} ({locale.localeCode})
                </option>
              ))}
            </select>
            {selectedLanguages.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Select at least one language to choose a regional accent
              </p>
            )}
            {selectedLanguages.length > 0 && availableLocales.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                No locales available for the selected language(s). Please configure locales first.
              </p>
            )}
            {errors.locale && (
              <p className="text-red-500 text-xs mt-1" data-testid="locale-error">
                {errors.locale}
              </p>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div
              className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded"
              data-testid="submit-error"
            >
              {errors.submit}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              data-testid="cancel-button"
              disabled={createVoiceMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400"
              data-testid="save-button"
              disabled={createVoiceMutation.isPending || isGenerating || isGeneratingSample}
            >
              {isGeneratingSample
                ? 'Generating Sample...'
                : createVoiceMutation.isPending
                  ? 'Saving...'
                  : isGenerating
                    ? 'Generating...'
                    : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
